import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { isItemAvailable } from 'entities/CatalogItem/lib/itemAvailability';
import { resolveCatalogRowSelection } from 'entities/CatalogItem/lib/resolveCatalogRowSelection';
import { getSelectedCatalogItems } from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import {
    CatalogItem,
    DisplayItem,
    GlobalCatalogItem,
} from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { Category, getLocalCategoriesList } from 'entities/Category';
import { getPropertyVendorsMap } from 'entities/Vendors';
import { useCatalogIdentity } from 'shared/lib/contexts/CatalogIdentityContext';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { PropertyCatalogItemDetailed } from 'shared/ui/PropertyCatalogItemDetailed/PropertyCatalogItemDetailed';
import { PropertyCatalogItemsMoveTo } from 'widgets/PropertyCatalog/models/consts/propertyCatalogItemsListTypes';

import { PropertyCatalogItem } from './PropertyCatalogItem';

export interface PropertyCatalogItemDataDefinerProps {
    item: CatalogItem | GlobalCatalogItem;
    quickEdit?: boolean;
    moveTo?: PropertyCatalogItemsMoveTo;
    isDetailed?: boolean;
    isOrderPage?: boolean;
    isSearch?: boolean;
}

const buildCategoryString = (item: GlobalCatalogItem) => {
    if (item.category.parentCategory) {
        return `${item.category.parentCategory.catName} / ${item.category.catName}`;
    }
    return item.category.catName;
};

const buildPropertyCategoryString = (
    item: CatalogItem,
    categories: Record<string, Category>,
) => {
    const categoryName = categories?.[item.categoryId]?.title;
    const glCode = categories?.[item.categoryId]?.glCode;
    if (glCode) {
        return `${categoryName} | ${glCode}`;
    }
    return categoryName;
};

export const PropertyCatalogItemDataDefiner = memo(
    ({
        item,
        quickEdit,
        moveTo,
        isSearch,
        isDetailed,
        isOrderPage,
    }: PropertyCatalogItemDataDefinerProps) => {
        const dispatch = useAppDispatch();
        const selectedItems = useSelector(getSelectedCatalogItems);
        const vendorsMap = useSelector(getPropertyVendorsMap);
        const categories = useSelector(getLocalCategoriesList);
        const catalogIdentity = useCatalogIdentity();
        const isLocalCatalogItem = 'id' in item;

        const rowId = isLocalCatalogItem ? item.id : item.uid;

        const selection = useMemo(
            () =>
                resolveCatalogRowSelection(
                    selectedItems,
                    item,
                    catalogIdentity,
                ),
            [selectedItems, item, catalogIdentity],
        );

        const itemUid = selection.canonicalKey;
        const resolvedSelectedEntry = selection.entry;

        const itemIsInOrder = useMemo(
            () => Boolean(resolvedSelectedEntry?.orderItemId),
            [resolvedSelectedEntry],
        );

        /** Order line id when hydrated; otherwise same stable id as `selectionKey` / `getItemUid` (not raw `item.id`). */
        const idToEdit = useMemo(() => {
            return resolvedSelectedEntry?.orderItemId ?? itemUid;
        }, [resolvedSelectedEntry, itemUid]);

        const price = isLocalCatalogItem ? item.price : 0;
        const isInventory = isLocalCatalogItem ? item.isInventory : false;

        const minAllowedInStock = isLocalCatalogItem
            ? item.minAllowedInStock
            : undefined;

        const isActive = resolvedSelectedEntry !== undefined;

        const qty = useMemo(
            () => resolvedSelectedEntry?.qty ?? 0,
            [resolvedSelectedEntry],
        );

        const displayItem: DisplayItem = useMemo(
            () => ({
                name: 'name' in item ? item.name : item.title,
                category: isLocalCatalogItem
                    ? buildPropertyCategoryString(item, categories)
                    : buildCategoryString(item),
                sku: item.sku,
                uom: item.uom,
                qtyPerUom: item.qtyPerUom,
                uomPrice: item.uomPrice,
                imageUrl: item.imageUrl,
                thumbnailUrl: item?.thumbnailUrl ?? '',
                manufacturerId: item.manufacturerId,
                mpn: item.mpn,
                upc: item.upc,
                brand: item?.brand ?? '',
                msrp: item.msrp,
                unspsc: item.unspsc,
                description: item.description,
                descriptionFormat: item.descriptionFormat,
                prop65Warning: item.prop65Warning,
                specifications: item.specifications,
                additionalImages: item.additionalImages,
                proposals: item?.proposals ?? [],
                glCode: isLocalCatalogItem
                    ? categories?.[item.categoryId]?.glCode
                    : undefined,
                isAddedToCatalog: item?.isAddedToCatalog ?? false,
            }),
            [item, categories, isLocalCatalogItem],
        );

        const onCancelDetailedView = useCallback(() => {
            dispatch(catalogItemActions.setShowDetailedView(false));
        }, [dispatch]);

        const isAvailable = useMemo(
            () => isItemAvailable(displayItem.proposals, vendorsMap),
            [displayItem.proposals, vendorsMap],
        );

        return isDetailed ? (
            <PropertyCatalogItemDetailed
                displayItem={displayItem}
                onCancel={onCancelDetailedView}
                price={price}
                moveTo={moveTo}
                id={rowId}
                isOrderPage={isOrderPage}
                minAllowedInStock={minAllowedInStock}
                isLocal={isLocalCatalogItem}
            />
        ) : (
            <PropertyCatalogItem
                key={itemUid}
                catalogItemId={isLocalCatalogItem ? item.id : undefined}
                propertyCategoryId={
                    isLocalCatalogItem ? item.categoryId : undefined
                }
                itemUid={itemUid}
                isActive={isActive}
                isInOrder={itemIsInOrder}
                isOrderPage={isOrderPage}
                quickEdit={quickEdit}
                moveTo={moveTo}
                id={rowId}
                selectionKey={selection.mapKey}
                idToEdit={idToEdit}
                title={displayItem.name}
                brand={displayItem.brand}
                thumbnailUrl={displayItem.thumbnailUrl}
                isSearch={isSearch}
                qty={qty}
                price={price}
                uom={displayItem.uom}
                qtyPerUom={displayItem.qtyPerUom}
                isInventory={isInventory}
                proposals={displayItem.proposals}
                isAvailable={isAvailable}
                isAddedToCatalog={displayItem.isAddedToCatalog}
            />
        );
    },
);
